import { friendlyApiError } from '../../../services/errorMessages';
import type {
  LiveRecordingFilter, LiveRecordingItem, LiveRecordingState, LiveRecordingSummary,
} from '../../../types/liveRecordings';

/**
 * CORE-1133 — слова экрана «Записи Live-сессий», отдельно от разметки.
 *
 * Статус записи — то место, где ошибка дороже всего: «Опубликована» на
 * необработанной записи зовёт открыть то, чего нет, а тихое отсутствие карточки
 * у сорвавшейся записи оставляет мастера гадать, куда делся эфир. Ровно этого
 * до сих пор и не было — состояние записи у Live отсутствовало как данные, и
 * «не записывали» с «не получилось» выглядели одинаково.
 */

const MONTHS_GENITIVE = [
  'янв.', 'фев.', 'мар.', 'апр.', 'мая', 'июн.',
  'июл.', 'авг.', 'сен.', 'окт.', 'ноя.', 'дек.',
];

/**
 * «24 авг. 2026 • 37 мин (план 60) • 128 просмотров» — строка под названием.
 *
 * CORE-1526 (п. 7) — `durationMinutes` в ответе архива — ПЛАНОВАЯ длительность:
 * её мастер выбрал при создании. Без подписи одна и та же сессия читалась как
 * «60 мин» здесь, «37:29» в «Совместных практиках» и «Длилось 37 мин» на
 * странице записи. Главное число теперь — длительность самой записи
 * (`recordingDurationSeconds`, бэкенд добавил 30.09), плановая подписана
 * отдельно. Пока записи нет (обрабатывается, сорвалась, старый бэкенд) —
 * только плановая, названная плановой.
 */
export function recordingLine(
  item: Pick<LiveRecordingItem, 'startDateTimeUtc' | 'durationMinutes' | 'recordingDurationSeconds' | 'watchedCount' | 'recordingState'>,
): string {
  const d = new Date(item.startDateTimeUtc);
  if (Number.isNaN(d.getTime())) return '';
  const parts = [`${d.getDate()} ${MONTHS_GENITIVE[d.getMonth()]} ${d.getFullYear()}`];
  const duration = durationLabel(item);
  if (duration) parts.push(duration);
  // У необработанной записи просмотров быть не может: её ещё никто не видел.
  // Печатать «0 просмотров» там — сообщать о провале там, где ничего не
  // случилось.
  parts.push(item.recordingState === 'ready' ? viewsLabel(item.watchedCount) : '—');
  return parts.join(' • ');
}

/**
 * «37 мин (план 60)», «меньше минуты (план 20)» или «план 60 мин».
 * Пустая строка — ни того, ни другого нет.
 */
export function durationLabel(item: Pick<LiveRecordingItem, 'durationMinutes' | 'recordingDurationSeconds'>): string {
  const planned = item.durationMinutes != null && item.durationMinutes > 0 ? item.durationMinutes : null;
  const seconds = item.recordingDurationSeconds;
  if (seconds != null && seconds > 0) {
    const actual = seconds < 60 ? 'меньше минуты' : `${Math.round(seconds / 60)} мин`;
    return planned != null ? `${actual} (план ${planned})` : actual;
  }
  return planned != null ? `план ${planned} мин` : '';
}

/**
 * CORE-1383 — запись без изображения. Комнату на Agora пишет аудиомост, и в
 * файле один звук; карточка с обложкой комнаты обещала видео, а плеер
 * показывал чёрный кадр. Бэкенд теперь отдаёт `hasVideo` (30.09); `null` —
 * ещё не замерено, и тогда карточка молчит, а не гадает.
 */
export const AUDIO_ONLY_LABEL = 'Только звук';

export function isAudioOnlyRecording(item: Pick<LiveRecordingItem, 'hasVideo'>): boolean {
  return item.hasVideo === false;
}

/** «1 просмотр» / «2 просмотра» / «128 просмотров». */
export function viewsLabel(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} просмотр`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} просмотра`;
  return `${count} просмотров`;
}

export interface StatusChip {
  label: string;
  tone: 'published' | 'draft' | 'processing' | 'failed';
}

/**
 * Что за чип у записи.
 *
 * Порядок проверок не случаен: состояние записи важнее её присутствия в
 * «Медиа». Запись, которая ещё обрабатывается, не может быть опубликована, и
 * показывать по ней «Опубликована» значило бы предложить открыть несуществующий
 * файл.
 */
export function statusChip(item: Pick<LiveRecordingItem, 'recordingState' | 'isPublished'>): StatusChip {
  // CORE-1133 (QA FAIL 15.09) — перечисление, а не отрицание.
  //
  // Было `if (recordingState !== 'ready') return «Обрабатывается»`. Отрицание
  // выглядит исчерпывающим и им не является: `recording` получал чужую подпись,
  // а сервер его при этом не считал ни одним счётчиком — мастер видел карточку
  // «Обрабатывается» при нуле в сводке.
  //
  // Теперь каждое состояние названо своим словом, а ветка `never` внизу делает
  // следующее добавленное состояние ОШИБКОЙ СБОРКИ, а не тихой подменой слова.
  switch (item.recordingState) {
    case 'failed':
      return { label: 'Не записалась', tone: 'failed' };
    case 'recording':
      // Эфир ещё идёт. «Обрабатывается» здесь было бы неправдой: обрабатывать
      // пока нечего, запись пишется.
      return { label: 'Идёт запись', tone: 'processing' };
    case 'processing':
      return { label: 'Обрабатывается', tone: 'processing' };
    case 'none':
      // В архив такие не попадают, но тип их допускает — и молчать о них
      // нельзя, иначе вернётся ровно тот же класс дефекта.
      return { label: 'Не записывалась', tone: 'failed' };
    case 'ready':
      return item.isPublished
        ? { label: 'Опубликована', tone: 'published' }
        : { label: 'Черновик', tone: 'draft' };
    default: {
      const unreachable: never = item.recordingState;
      return unreachable;
    }
  }
}

/** Можно ли открыть запись — только когда есть что открывать. */
export function canOpen(item: Pick<LiveRecordingItem, 'recordingState' | 'playbackUrl'>): boolean {
  return item.recordingState === 'ready' && !!item.playbackUrl;
}

export interface PrimaryAction {
  label: string;
  enabled: boolean;
  /** Куда ведёт: в плеер записи или на страницу сессии с её чатом. */
  target: 'recording' | 'session';
}

/**
 * CORE-1526 (п. 3) — главное действие карточки.
 *
 * У сорвавшейся записи подсказка обещала, что «Live-сессия и её чат на месте»,
 * а единственная кнопка «Открыть запись» стояла выключенной, и в меню были
 * только шаблон и удаление: обещанное было, но дойти до него из карточки было
 * нечем. Теперь у записи без файла главное действие ведёт на саму сессию.
 *
 * `none` сюда попадает по той же причине: файла нет и не будет, а сессия есть.
 * Обрабатывающаяся и идущая запись остаются с выключенной кнопкой — там файл
 * ещё появится, и вести мимо него незачем.
 */
export function primaryAction(item: Pick<LiveRecordingItem, 'recordingState' | 'playbackUrl'>): PrimaryAction {
  if (item.recordingState === 'failed' || item.recordingState === 'none') {
    return { label: 'Открыть сессию и чат', enabled: true, target: 'session' };
  }
  return { label: 'Открыть запись', enabled: canOpen(item), target: 'recording' };
}

export const FILTERS: { key: LiveRecordingFilter; label: string }[] = [
  { key: 'all', label: 'Все записи' },
  { key: 'published', label: 'Опубликованные' },
  { key: 'draft', label: 'Черновики' },
  { key: 'processing', label: 'Обрабатываются' },
  { key: 'failed', label: 'Не записались' },
];

/** Что показать вместо списка, когда его нет. */
export function emptyLabel(filter: LiveRecordingFilter, searching: boolean): string {
  if (searching) return 'По этому запросу записей не нашлось.';
  switch (filter) {
    case 'published': return 'В «Медиа» пока ничего не опубликовано.';
    case 'draft': return 'Черновиков нет — всё опубликовано или ещё обрабатывается.';
    case 'processing': return 'Ничего не обрабатывается.';
    case 'failed': return 'Все записи на месте — ни одна не сорвалась.';
    default:
      // Эфир без записи в архив не попадает, и это не пустой список, а
      // отсутствие записей вообще — так и говорим.
      return 'Записей пока нет. Они появляются после Live-сессий, которые вы записывали.';
  }
}

/**
 * CORE-1133 (QA FAIL 23.09) — подпись кнопки догрузки. Число показанных из
 * общего — чтобы мастер видел, что список не кончился, и сколько осталось.
 */
export function loadMoreLabel(shown: number, total: number, failed: boolean): string {
  if (failed) return 'Не удалось загрузить — повторить';
  return `Показать ещё · ${shown} из ${total}`;
}

/** Объяснение к сорвавшейся записи — без него мастер видит просто «нет файла». */
export const FAILED_HINT =
  'Запись велась, но файл не получился. Live-сессия и её чат на месте — открыть их можно кнопкой рядом.';

/** Подписи сводки в правой колонке. */
/**
 * Подписи сводки в правой колонке.
 *
 * CORE-1133 (16.09) — пятая строка появилась вместе с пятым фильтром, и по
 * одной причине: до неё сводка не сходилась. На деве она читалась как
 * «Всего 170 · Опубликовано 1 · Черновики 106 · Обрабатываются 0» — 63 записи
 * пропадали между «всего» и слагаемыми, и мастеру оставалось гадать, где они.
 * Ячейка без фильтра назвала бы число, до которого не дойти; фильтр без ячейки
 * оставил бы арифметику сломанной. Поэтому обе сразу.
 */
export const SUMMARY_ROWS: {
  key: 'total' | 'published' | 'drafts' | 'processing' | 'failed';
  label: string;
}[] = [
  { key: 'total', label: 'Всего записей' },
  { key: 'published', label: 'Опубликовано' },
  { key: 'drafts', label: 'Черновики' },
  { key: 'processing', label: 'Обрабатываются' },
  { key: 'failed', label: 'Не записались' },
];

/**
 * CORE-1526 (п. 8) — строки сводки со значениями, и все состояния в ней.
 *
 * Аудит 28.09 (staging): «Всего 105», а слагаемые давали 65, и что стоит за
 * остальными сорока, нигде не говорилось. Пятая строка «Не записались»
 * (CORE-1133) закрыла главную дыру, но одно состояние архива сервер не
 * считает ни одним счётчиком — идущую запись (см. `statusChip`). Такой
 * остаток больше не пропадает молча: он становится своей строкой, и сумма
 * строк всегда равна «Всего».
 *
 * Остаток называется «Идёт запись», потому что других состояний в архиве не
 * бывает: `none` (не записывали) в него не попадает по контракту списка.
 */
export interface SummaryRow {
  key: 'total' | 'published' | 'drafts' | 'processing' | 'failed' | 'recording';
  label: string;
  value: number;
}

export function summaryRows(summary: Partial<LiveRecordingSummary>): SummaryRow[] {
  const rows: SummaryRow[] = SUMMARY_ROWS.map((r) => ({ key: r.key, label: r.label, value: summary[r.key] ?? 0 }));
  const counted = rows.filter((r) => r.key !== 'total').reduce((sum, r) => sum + r.value, 0);
  const rest = (summary.total ?? 0) - counted;
  if (rest > 0) rows.push({ key: 'recording', label: 'Идёт запись', value: rest });
  return rows;
}

/** Как читать сводку — одной строкой под ней. */
export const SUMMARY_NOTE =
  'Каждая запись учтена в одной строке, вместе они дают «Всего». Live-сессии без записи в архив не попадают.';

/** Подтверждение удаления: главное здесь — что уходит только запись. */
export const DELETE_TITLE = 'Удалить запись?';
export const DELETE_BODY =
  'Файл записи пропадёт безвозвратно, и из «Медиа» она тоже исчезнет.'
  + '\n\nСама Live-сессия останется: её чат, статистика и место в вашей истории никуда не денутся.';

/**
 * CORE-1526 (п. 2) — два удаления, два имени, и оба называют объект.
 *
 * Аудит нашёл три подписи на одно и то же действие: «Удалить запись» в архиве,
 * «Удалить сессию» на странице записи, «Удалить Live-сессию» в «Совместных
 * практиках» — при том, что первое убирает только файл, а два других уносят
 * сессию целиком с чатом и историей. Разница не была объяснена нигде.
 *
 * Имя действия теперь одно на объект: «Удалить запись» везде, где уходит
 * только запись, «Удалить Live-сессию» везде, где уходит сессия. Тело
 * подтверждения перечисляет судьбу файла, чата, истории и публикации — и
 * называет путь, которым можно убрать только запись.
 */
export const RECORDING_DELETE_ACTION = 'Удалить запись';
export const SESSION_DELETE_ACTION = 'Удалить Live-сессию';
export const SESSION_DELETE_TITLE = 'Удалить Live-сессию?';

export function sessionDeleteBody(
  title: string | null | undefined,
  options: {
    /**
     * CORE-1526 (ревью) — подсказка про «только запись» ведёт в раздел
     * «Записи», которого у обычного пользователя нет: ему её не показываем.
     */
    recordingOnlyPath?: boolean;
  } = {},
): string {
  const name = title?.trim() ? ` «${title.trim()}»` : '';
  const body = `Live-сессия${name} будет удалена целиком: вместе с ней уйдут запись, чат, статистика и место в вашей истории. Из «Медиа» запись тоже исчезнет.`;
  if (options.recordingOnlyPath === false) return body;
  return body + '\n\nЧтобы убрать только запись, а сессию оставить, откройте раздел «Записи» → «Удалить запись».';
}

export function stateIsTerminal(state: LiveRecordingState): boolean {
  return state === 'ready' || state === 'failed';
}

/**
 * CORE-1257 — итог действия в архиве. Раньше итогом была одна строка, а
 * заголовок окна всегда говорил «Готово»: отказ сервера читался как
 * «Готово / Failed to delete recording». Тон и заголовок едут вместе с текстом.
 */
export interface RecordingsNotice {
  tone: 'success' | 'error';
  title: string;
  message: string;
}

export const successNotice = (message: string): RecordingsNotice => ({ tone: 'success', title: 'Готово', message });

export function failureNotice(title: string, err: unknown): RecordingsNotice {
  return { tone: 'error', title, message: friendlyApiError(err) };
}

/**
 * Удаление не прошло. 404 — отдельный случай: сервер так отвечает, когда самой
 * Live-сессии уже нет (её удалили целиком, например с другого устройства), а
 * значит и записи нет. Это не повод предлагать повтор — список надо перечитать.
 */
export function deleteFailedNotice(err: unknown): RecordingsNotice {
  if ((err as { status?: number } | null)?.status === 404) {
    return {
      tone: 'error',
      title: 'Записи уже нет',
      message: 'Эта Live-сессия уже удалена вместе с записью. Список обновлён.',
    };
  }
  return failureNotice('Не удалось удалить запись', err);
}
